// Logic is evaluated for required and optional questions alike: an optional
// question that was left empty (or skipped) still routes via notAnswered,
// notContain etc. `answer` may be null/undefined for a skipped question.
export function checkRouteLogic(answer, question, questions, currentIndex) {
    let normalizedAnswer = normalizeAnswer(answer, question);

    const areAnagrams = (str1, str2) =>
        str1.toLowerCase().split('').sort().join('') === str2.toLowerCase().split('').sort().join('');

    if (question.type === 'Net Promoter Score') {
        normalizedAnswer = Number(normalizedAnswer);
        if (!isNaN(normalizedAnswer)) {
            if (question.colorScale === 'new5faces') {
                normalizedAnswer = normalizedAnswer <= 3 ? 1 : normalizedAnswer === 4 ? 2 : 3;
            } else {
                normalizedAnswer = normalizedAnswer < 7 ? 1 : normalizedAnswer >= 7 && normalizedAnswer <= 8 ? 2 : 3;
            }
        }
    }

    // Multi Select answers are matched per selected option, so a logic
    // configured for one option applies even when more options are selected
    const answerVariants =
        question.type === 'Multi Select' && Array.isArray(answer)
            ? answer.map((item) => item.toString().toLowerCase().replaceAll(' ', '').trim())
            : [normalizedAnswer];

    const normalizePresumption = (value) => value.toString().toLowerCase().replaceAll(' ', '').trim();

    // The builder stores several checked options of a Single/Multi Select
    // logic as "id1, id2" in `presumption`; every id is resolved to its option
    // text. Other question types carry the compared text directly.
    const resolvePresumptions = (logic) => {
        const raw = logic.presumption === null || logic.presumption === undefined ? '' : logic.presumption.toString();

        if (question.type === 'Multi Select' || question.type === 'Single Select') {
            const options = question.answers || [];
            return raw
                .split(',')
                .map((part) => part.trim())
                .filter((part) => part !== '')
                .map((id) => {
                    const option = options.find((ans) => ans.id === id);
                    const text = option ? (option.type === 'other' ? option.name : option.content) : id;
                    return normalizePresumption(text ?? '');
                });
        }

        return [normalizePresumption(raw)];
    };

    const logicMatches = (logic) => {
        const presumptions = resolvePresumptions(logic);
        const presumption = presumptions[0] ?? '';
        const anyPresumption = (test) => presumptions.some((p) => answerVariants.some((variant) => test(variant, p)));

        switch (logic.logicCondition) {
            case 'contain':
                return anyPresumption((variant, p) => variant.toString().includes(p));
            case 'notContain':
                return !anyPresumption((variant, p) => variant.toString().includes(p));
            case 'exact':
                return anyPresumption((variant, p) => variant === p);
            case 'notExact':
                return !anyPresumption((variant, p) => variant === p);
            case 'equal':
                return anyPresumption((variant, p) => areAnagrams(variant.toString(), p));
            case 'notEqual':
                return !anyPresumption((variant, p) => areAnagrams(variant.toString(), p));
            case 'exactly':
                return typeof normalizedAnswer === 'number'
                    ? presumptions.some((p) => normalizedAnswer === Number(p))
                    : anyPresumption((variant, p) => areAnagrams(variant.toString(), p));
            case 'betterThanOrEqual':
                return normalizedAnswer === presumption || normalizedAnswer > presumption;
            case 'worseThanOrEqual':
                return normalizedAnswer === presumption || normalizedAnswer < presumption;
            case 'answered':
                return normalizedAnswer !== '';
            case 'notAnswered':
                return normalizedAnswer === '';
            case 'emailFilled':
                return question.type === 'Mailing' && normalizedAnswer !== '';
            case 'emailSkipped':
                return question.type === 'Mailing' && normalizedAnswer === '';
            default:
                return false;
        }
    };

    const logics = question.logics || [{ routing: null, presumption: null, logicCondition: null }];

    for (const logic of logics) {
        if (logic.logicCondition === 'otherAnswer') {
            // "Alle anderen" is the fallback: it only applies when no specific
            // logic matches the answer, regardless of its position in the config
            const specificMatches = logics.some(
                (other) => other.logicCondition !== 'otherAnswer' && logicMatches(other)
            );
            if (!specificMatches) {
                return handleSettingNextQuestion(logic, questions, currentIndex);
            }
            continue;
        }

        if (logicMatches(logic)) {
            return handleSettingNextQuestion(logic, questions, currentIndex);
        }
    }

    return { nextIndex: currentIndex + 1, skippedIds: [] };
}

function normalizeAnswer(answer, question) {
    if (answer === null || answer === undefined) {
        return '';
    }
    if (typeof answer === 'object' && question.type !== 'Ranking') {
        return answer.join('').toLowerCase().replaceAll(' ', '').trim();
    } else if (question.type !== 'Ranking' && question.type !== 'Matrix') {
        return answer.toString().toLowerCase().replaceAll(' ', '').trim();
    }
    return answer;
}

export function handleSettingNextQuestion(logic, questions, currentIndex) {
    const skippedIds = [];

    switch (logic.routing) {
        case 'schließe die Umfrage':
            return { nextIndex: null, skippedIds, closeSurvey: true };

        case 'zeige die Thank You Message':
            return { nextIndex: questions.length - 1, skippedIds };

        case 'weiter zur nächsten Frage': {
            const nextIdx = currentIndex + 1;
            const nextQ = questions[nextIdx];
            if (nextQ) {
                skippedIds.push(nextQ.properties ? nextQ.properties.id : nextQ.id);
            }
            return { nextIndex: nextIdx, skippedIds };
        }

        case 'gehe zu beliebiger Frage': {
            const num = Math.floor(Math.random() * (questions.length - 1)) + 1;
            const targetQ = questions[num];
            if (targetQ) {
                skippedIds.push(targetQ.properties ? targetQ.properties.id : targetQ.id);
            }
            return { nextIndex: num, skippedIds };
        }

        case 'close the survey':
            return { nextIndex: null, skippedIds, closeSurvey: true };

        default: {
            const index = questions.findIndex((question) => {
                let id = question.properties ? question.properties.id : question.id;
                return id === logic.routing;
            });
            const nextIdx = index !== -1 ? index : currentIndex + 1;
            skippedIds.push(logic.routing);
            return { nextIndex: nextIdx, skippedIds };
        }
    }
}
